/**
 * Browser plumbing for Ocean Payment's inline card form (Oceanpayment
 * Embedded SDK). Mirrors the hosted checkout's OceanPaymentPanel
 * (apps/studio-app/.../PaymentMethodSelector/TakeoverPanels.tsx).
 */

export interface OceanpaymentGlobal {
  init: (sandbox: boolean, cssUrl: string, language: string, configuration: Record<string, unknown>) => void;
  checkout: (data: Record<string, string>) => void;
}

export type OceanWindow = Window & {
  Oceanpayment?: OceanpaymentGlobal;
  /** Ocean's SDK calls this global — the name is fixed by the SDK. */
  oceanpaymentCallBack?: (data: unknown) => void;
};

export const OCEAN_SDK_URL = 'https://secure.oceanpayment.com/pages/js/oceanpayment.js';
/** Fixed by Ocean's SDK, which looks the container up by this id. */
export const OCEAN_CONTAINER_ID = 'oceanpayment-element';
/** Ocean's iframe answers every checkout(); this only guards a lost message. */
export const OCEAN_CALLBACK_TIMEOUT_MS = 120_000;

/**
 * Ocean builds its card fields server-side from `?language=`. An unknown code
 * falls back to English, and `fi` comes back in Polish, so Finnish shoppers
 * get English.
 */
const OCEAN_LANGUAGE_OVERRIDES: Record<string, string> = { fi: 'en' };

export function oceanLanguage(locale: string | undefined): string {
  const primary = locale?.split(/[-_]/)[0]?.toLowerCase();
  if (!primary) return 'en';
  return OCEAN_LANGUAGE_OVERRIDES[primary] ?? primary;
}

const OCEAN_SDK_LOAD_TIMEOUT_MS = 30_000;
let oceanSdkLoading: Promise<OceanpaymentGlobal> | null = null;

/**
 * Load Ocean's SDK once per page: a second copy would deliver every result
 * twice. A failed or stalled load is forgotten, so the next mount retries.
 */
export function loadOceanSdk(): Promise<OceanpaymentGlobal> {
  const w = window as OceanWindow;
  if (w.Oceanpayment) return Promise.resolve(w.Oceanpayment);
  if (oceanSdkLoading) return oceanSdkLoading;

  oceanSdkLoading = new Promise<OceanpaymentGlobal>((resolve, reject) => {
    const script = document.createElement('script');
    const fail = (message: string) => {
      clearTimeout(timer);
      script.remove();
      oceanSdkLoading = null;
      reject(new Error(message));
    };
    const timer = setTimeout(() => fail('The Ocean Payment card form took too long to load'), OCEAN_SDK_LOAD_TIMEOUT_MS);
    script.addEventListener('load', () => {
      if (!w.Oceanpayment) return fail('Ocean Payment script loaded without window.Oceanpayment');
      clearTimeout(timer);
      resolve(w.Oceanpayment);
    });
    script.addEventListener('error', () => fail('Failed to load the Ocean Payment card form'));
    script.src = OCEAN_SDK_URL;
    document.head.appendChild(script);
  });
  return oceanSdkLoading;
}

/**
 * Ocean's inline card form leaves the page only for 3-D Secure, and Ocean
 * returns the shopper to the exact page URL the form was opened on — without
 * any payment reference in it. The payment id is parked in sessionStorage
 * before leaving; a stale entry (abandoned 3DS) is ignored after 30 minutes.
 */
const OCEAN_PENDING_KEY = 'tgd:headless:oceanpayment:pending';
const OCEAN_PENDING_TTL_MS = 30 * 60 * 1000;

export function parkOceanPayment(paymentId: string): void {
  try {
    sessionStorage.setItem(OCEAN_PENDING_KEY, JSON.stringify({ paymentId, at: Date.now() }));
  } catch {
    // Storage unavailable — Ocean's webhook still settles the order.
  }
}

function takeParkedOceanPayment(): string | undefined {
  try {
    const raw = sessionStorage.getItem(OCEAN_PENDING_KEY);
    if (!raw) return undefined;
    sessionStorage.removeItem(OCEAN_PENDING_KEY);
    const { paymentId, at } = JSON.parse(raw) as { paymentId?: string; at?: number };
    if (!paymentId || !at || Date.now() - at > OCEAN_PENDING_TTL_MS) return undefined;
    return paymentId;
  } catch {
    return undefined;
  }
}

/**
 * Back from Ocean's 3-D Secure page: turn the parked payment into the
 * `paymentAction` / `paymentActionStatus` / `paymentId` markers that
 * `maybeResumeFromUrl()` and `usePayment()` already resume from.
 */
export function restoreOceanPaymentReturn(): void {
  if (typeof window === 'undefined') return;
  const paymentId = takeParkedOceanPayment();
  if (!paymentId) return;
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get('paymentId')) return;
    url.searchParams.set('paymentId', paymentId);
    url.searchParams.set('paymentAction', 'requireAction');
    url.searchParams.set('paymentActionStatus', 'completed');
    window.history.replaceState(window.history.state, '', url.toString());
  } catch {
    // Best-effort: Ocean's webhook still settles the order.
  }
}
